import React, { useContext } from 'react';
import {
  Box,
  Grid,
  Typography,
  useTheme,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { System } from '../../enums/System';
import {
  PublicOutlined, FastfoodOutlined, ScaleOutlined, AutoFixHighOutlined, StyleOutlined, LocalShipping,
  ScheduleSend, EditCalendar, EventRepeat, Task, Category, AccountTree, Newspaper, EditCalendarOutlined,
} from '@mui/icons-material';
import {
  Users01, PackageCheck, CoinsSwap02, Calendar, User01, Announcement02,
  Settings01, Grid01, Tag03,
  Truck01, ListIcon, Building02, ChevronRight,
} from '../../assets';
import { CurrencyExchange } from '@mui/icons-material';
import { Role } from '../../enums/Role';

interface SettingItem {
  translationKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  href?: string;
  permissions?: Role[];
  systemIds: number[];
}

interface SettingSection {
  titleKey: string;
  descriptionKey: string;
  items: SettingItem[];
}

const SettingsPage: React.FC = () => {
  const { t } = useTranslation('drawer');
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermissionTo, selectedSystem, systems } = useContext(UserContext);

  const settingSections: SettingSection[] = [
    {
      titleKey: 'userAccessManagement',
      descriptionKey: 'userAccessManagementDesc',
      items: [
        {
          translationKey: 'manageApplicationRoles',
          descriptionKey: 'manageApplicationRolesDesc',
          icon: <Users01 />,
          href: '/role-management',
          permissions: ['RolesAdmin', 'RoleManagementView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'manageUsers',
          descriptionKey: 'manageUsersDesc',
          icon: <User01 />,
          href: '/user-management',
          permissions: ['UserAdmin', 'UserManagementView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'operationsAutomation',
      descriptionKey: 'operationsAutomationDesc',
      items: [
        {
          translationKey: 'operationalDaySetup',
          descriptionKey: 'operationalDaySetupDesc',
          icon: <EditCalendar />,
          href: '/operational-days',
          permissions: ['OperationalDaysView'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'scheduledTasks',
          descriptionKey: 'scheduledTasksDesc',
          icon: <EventRepeat />,
          href: '/task-schedules',
          permissions: ['TaskSchedulesView'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'taskTemplates',
          descriptionKey: 'taskTemplatesDesc',
          icon: <Task />,
          href: '/task-templates',
          permissions: ['TaskTemplatesViewUnrestricted'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'scheduledReports',
          descriptionKey: 'scheduledReportsDesc',
          permissions: ['AutomatedTasks'],
          icon: <ScheduleSend />,
          href: '/scheduled-reports',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'automatedWorkflows',
          descriptionKey: 'automatedWorkflowsDesc',
          icon: <AccountTree />,
          href: '/automated-workflows',
          permissions: ['AutomatedWorkflowsView', 'ConfigurationAdmin'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'autoshipments',
          descriptionKey: 'autoshipmentsDesc',
          icon: <LocalShipping />,
          href: '/manage-autoshipments/templates',
          permissions: ['AutoshipmentRead'],
          systemIds: [System.OrderlyForStores],
        },
      ],
    },
    {
      titleKey: 'newsAndCommunications',
      descriptionKey: 'newsAndCommunicationsDesc',
      items: [
        {
          translationKey: 'manageNotifications',
          descriptionKey: 'manageNotificationsDesc',
          icon: <Announcement02 />,
          href: '/manage-notifications',
          permissions: ['NotificationWrite'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'newsManagement',
          descriptionKey: 'newsManagementDesc',
          icon: <Newspaper />,
          href: '/news-management',
          permissions: ['NewsManagementView', 'ConfigurationAdmin'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'systemConfiguration',
      descriptionKey: 'systemConfigurationDesc',
      items: [
        {
          translationKey: 'systemConfigurations',
          descriptionKey: 'systemConfigurationsDesc',
          icon: <Settings01 />,
          permissions: ['ConfigurationAdmin'],
          href: '/manage-configurations',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'manageTransactions',
          descriptionKey: 'manageTransactionsDesc',
          icon: <CoinsSwap02 />,
          href: '/manage-transactions',
          permissions: ['TransactionAdmin'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'manageTaskCategories',
          descriptionKey: 'manageTaskCategoriesDesc',
          icon: <Category />,
          href: '/manage-task-categories',
          permissions: ['TaskCategoriesView'],
          systemIds: [System.OrderlyForStores],
        },

      ],
    },
    {
      titleKey: 'businessStructure',
      descriptionKey: 'businessStructureDesc',
      items: [
        {
          translationKey: 'instances',
          descriptionKey: 'instancesDesc',
          icon: <Grid01 />,
          permissions: ['InstanceView', 'ConfigurationAdmin'],
          href: '/instances',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'markets',
          descriptionKey: 'marketsDesc',
          icon: <PublicOutlined />,
          permissions: ['MarketView', 'ConfigurationAdmin'],
          href: '/markets',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        /*  {
            translationKey: 'storeGroups',
            descriptionKey: 'storeGroupsDesc',
            icon: <PublicOutlined />,
            permissions: ['NEWPERMISSION'],
            href: '/markets',
            systemIds: [System.OrderlyForStores],
          }, */
        {
          translationKey: 'stores',
          descriptionKey: 'storesDesc',
          icon: <Building02 />,
          permissions: ['StoreView', 'ConfigurationAdmin'],
          href: '/stores',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'productCatalogManagement',
      descriptionKey: 'productCatalogManagementDesc',
      items: [
        {
          translationKey: 'orderTypes',
          descriptionKey: 'orderTypesDesc',
          icon: <StyleOutlined />,
          permissions: ['OrderTypeView', 'ConfigurationAdmin'],
          href: '/order-types',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'manageDeliverySchedules',
          descriptionKey: 'manageDeliverySchedulesDesc',
          icon: <EditCalendarOutlined />,
          href: '/manage-delivery-schedules',
          permissions: ['ODSRead'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'suppliers',
          descriptionKey: 'suppliersDesc',
          icon: <Truck01 />,
          permissions: ['SupplierView', 'ConfigurationAdmin'],
          href: '/suppliers',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'catalogues',
          descriptionKey: 'cataloguesDesc',
          icon: <ListIcon />,
          permissions: ['CatalogueView', 'ConfigurationAdmin'],
          href: '/catalogues',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'uomTypes',
          descriptionKey: 'uomTypesDesc',
          icon: <ScaleOutlined />,
          permissions: ['UomTypeView', 'ConfigurationAdmin'],
          href: '/uom-types',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'categories',
          descriptionKey: 'categoriesDesc',
          icon: <Tag03 />,
          permissions: ['CategoryView', 'ConfigurationAdmin'],
          href: '/categories',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'products',
          descriptionKey: 'productsDesc',
          icon: <FastfoodOutlined />,
          permissions: ['ProductView', 'ConfigurationAdmin'],
          href: '/products',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'manageCategory',
          descriptionKey: 'manageCategoryDesc',
          icon: <Grid01 />,
          href: '/manage-categories',
          permissions: ['CategoryAdmin'],
          systemIds: [System.OrderlyForStores],
        },
      ],
    },
    {
      titleKey: 'advancedFeatures',
      descriptionKey: 'advancedFeaturesDesc',
      items: [
        {
          translationKey: 'podSettings',
          descriptionKey: 'podSettingsDesc',
          icon: <AutoFixHighOutlined />,
          permissions: ['AIOrderingAdmin'],
          href: '/manage-pod-configurations',
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'manageExchangeRates',
          descriptionKey: 'manageExchangeRatesDesc',
          icon: <CurrencyExchange />,
          href: '/manage-exchange-rates',
          permissions: ['UserAdmin', 'UserManagementView'],
          systemIds: [System.OrderlyForDistributors],
        },
      ],
    },
  ];

  const canRenderItem = (item: SettingItem): boolean => {
    const userCanAccessPage = !item.permissions || hasPermissionTo(item.permissions);
    const userCanAccessSystem = item.systemIds?.some((systemId) => systemId === selectedSystem?.id) ?? false;
    const systemExists = systems.some(x => x.id === 0) || userCanAccessSystem;

    return userCanAccessPage && systemExists;
  };

  const handleItemClick = (item: SettingItem) => {
    if (item.href) {
      navigate(item.href);
    }
  };


  const renderSettingsCard = (item: SettingItem) => (
    <Paper
      key={item.translationKey}
      elevation={0}
      sx={{
        p: 3,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderRadius: 2,
        '&:hover': {
          backgroundColor: theme.palette.primary[50],
        },
      }}
      onClick={() => handleItemClick(item)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
        <Box
          sx={{
            width: 48,
            height: '100%',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
            flexShrink: 0,
          }}
        >
          {item.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h6"
            sx={{ fontWeight: 600, mb: 1 }}>
            {t(item.translationKey)}
          </Typography>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}>
            {t(item.descriptionKey, `Configure ${t(item.translationKey).toLowerCase()} settings and preferences.`)}
          </Typography>
        </Box>
        <ChevronRight
          style={{ color: theme.palette.primary.contrastText, marginTop: 1 }} />
      </Box>
    </Paper>
  );

  const renderSettingsSection = (section: SettingSection) => {
    const visibleItems = section.items.filter(canRenderItem);

    if (visibleItems.length === 0) {
      return null;
    }

    return (
      <Box key={section.titleKey}
        sx={{ mb: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Typography component="h5"
            sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
            {t(section.titleKey, section.titleKey)}
          </Typography>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}>
            {t(section.descriptionKey, `Manage ${t(section.titleKey).toLowerCase()} settings and configurations.`)}
          </Typography>
        </Box>
        <Grid container
          spacing={3}>
          {visibleItems.map((item) => (
            <Grid item
              xs={12}
              md={6}
              lg={4}
              key={item.translationKey}>
              {renderSettingsCard(item)}
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };


  return (
    <PageArea>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <PageTitle>{t('settings')}</PageTitle>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mt: 1 }}>
            {t('settingsDescription', 'Manage your application settings, configurations, and preferences from this central location.')}
          </Typography>
        </Box>
      </Box>

      <Box>
        {settingSections.map(renderSettingsSection)}
      </Box>
    </PageArea>
  );
};

export default SettingsPage;